import { Table, Form, Button } from 'react-bootstrap';
import { useGetStoreIncomesQuery } from './../slices/storeIncomesApiSlice';
import { useGetStoreExpensesQuery } from './../slices/storeExpensesApiSlice';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const storeIncomeCategories = ["Morning", "AfterNoon", "Night", "Other"];
const storeExpenseCategories = [
  "StoreSalesCredit", "StoreSalesPayment", "Consult", "Lab", "Dressing", "Delivery",
  "Observation", "Admission", "Other", "Electricity", "StoreExpense", "Service", "Office StoreSupplies"
];

const StoreIncomeStoreExpenseCategories = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: storeIncomesData } = useGetStoreIncomesQuery({ startDate, endDate });
  const { data: storeExpensesData } = useGetStoreExpensesQuery({ startDate, endDate });

  // Group by date
  const groupByDate = (data, categories) => {
    const grouped = {};
    data?.forEach(entry => {
      const date = new Date(entry.date).toISOString().split('T')[0];
      if (date >= startDate && date <= endDate) {
        if (!grouped[date]) grouped[date] = {};
        const cat = entry.category;
        const amt = Number(entry.amount || 0);
        grouped[date][cat] = (grouped[date][cat] || 0) + amt;
      }
    });
    return grouped;
  };

  const groupedStoreIncomeByDate = groupByDate(storeIncomesData?.storeIncomes, storeIncomeCategories);
  const groupedStoreExpenseByDate = groupByDate(storeExpensesData?.storeExpenses, storeExpenseCategories);

  const getColumnTotals = (grouped, categories) => {
    const totals = {};
    Object.values(grouped).forEach(entry => {
      categories.forEach(cat => {
        totals[cat] = (totals[cat] || 0) + (entry[cat] || 0);
      });
    });
    return totals;
  };

  const storeIncomeColumnTotals = getColumnTotals(groupedStoreIncomeByDate, storeIncomeCategories);
  const storeExpenseColumnTotals = getColumnTotals(groupedStoreExpenseByDate, storeExpenseCategories);

  const totalStoreIncome = Object.values(storeIncomeColumnTotals).reduce((sum, val) => sum + val, 0);
  const totalStoreExpenses = Object.values(storeExpenseColumnTotals).reduce((sum, val) => sum + val, 0);

  // Export to Excel
  const handleExport = () => {
    const buildSheet = (grouped, categories, label) => {
      const rows = Object.entries(grouped).map(([date, entry]) => {
        const row = { Date: date };
        let rowTotal = 0;
        categories.forEach(cat => {
          const val = entry[cat] || 0;
          row[cat] = val;
          rowTotal += val;
        });
        row[label] = rowTotal;
        return row;
      });

      const summary = { Date: 'Total' };
      categories.forEach(cat => {
        summary[cat] = Object.values(grouped).reduce((sum, entry) => sum + (entry[cat] || 0), 0);
      });
      summary[label] = rows.reduce((sum, r) => sum + r[label], 0);
      rows.push(summary);

      return XLSX.utils.json_to_sheet(rows);
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, buildSheet(groupedStoreIncomeByDate, storeIncomeCategories, 'Total StoreIncome'), 'StoreIncome Report');
    XLSX.utils.book_append_sheet(workbook, buildSheet(groupedStoreExpenseByDate, storeExpenseCategories, 'Total StoreExpenses'), 'StoreExpense Report');

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `StoreIncome_StoreExpense_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center">MiniIncome & StoreExpense Report</h1>

      <Form className="mb-3 d-flex justify-content-center">
        <Form.Group className="mx-2">
          <Form.Label>Start Date:</Form.Label>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>End Date:</Form.Label>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Form.Group>
      </Form>

      {startDate && endDate && (
        <>
          {/* StoreIncome Table */}
          <h4 className="mt-4">StoreIncome Report</h4>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Date</th>
                {storeIncomeCategories.map(cat => <th key={cat}>{cat}</th>)}
                <th>Total StoreIncome</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(groupedStoreIncomeByDate).map(([date, entry]) => {
                const rowTotal = storeIncomeCategories.reduce((sum, cat) => sum + (entry[cat] || 0), 0);
                return (
                  <tr key={date}>
                    <td>{date}</td>
                    {storeIncomeCategories.map(cat => <td key={cat}>{(entry[cat] || 0).toFixed(2)}</td>)}
                    <td>{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="fw-bold">
                <td>Total</td>
                {storeIncomeCategories.map(cat => <td key={cat}>{(storeIncomeColumnTotals[cat] || 0).toFixed(2)}</td>)}
                <td>{totalStoreIncome.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          {/* StoreExpense Table */}
          <h4 className="mt-5">StoreExpense Report</h4>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Date</th>
                {storeExpenseCategories.map(cat => <th key={cat}>{cat}</th>)}
                <th>Total StoreExpenses</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(groupedStoreExpenseByDate).map(([date, entry]) => {
                const rowTotal = storeExpenseCategories.reduce((sum, cat) => sum + (entry[cat] || 0), 0);
                return (
                  <tr key={date}>
                    <td>{date}</td>
                    {storeExpenseCategories.map(cat => <td key={cat}>{(entry[cat] || 0).toFixed(2)}</td>)}
                    <td>{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="fw-bold">
                <td>Total</td>
                {storeExpenseCategories.map(cat => <td key={cat}>{(storeExpenseColumnTotals[cat] || 0).toFixed(2)}</td>)}
                <td>{totalStoreExpenses.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center mt-4">
            <Button variant="primary" onClick={handleExport}>
              Export Combined Report to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreIncomeStoreExpenseCategories;
